import { storeToRefs } from "pinia";
import {
  Paragraph,
  ParagraphFormattingChanges,
  Paragraphs,
} from "~/types/Paragraph";

export const useTokenFormatter = () => {
  const formatSelectedTokens = (
    documentId: string,
    formattingChanges: ParagraphTokenFormattingChanges
  ) => {
    const { selectedTokenPositions } = useDocumentSelectionStore(documentId);

    const tokensToSave = [] as ParagraphToken[];

    for (var tokenPosition of selectedTokenPositions) {
      const tokenStore = useParagraphTokenStore(documentId, tokenPosition);

      const token = tokenStore.token;

      if (token == undefined) continue;

      token.formatting = {
        ...token?.formatting,
        ...formattingChanges,
      };

      tokensToSave.push(token);
    }

    useDocumentParagraphsStore(documentId).saveTokens(tokensToSave);
  };

  const doesSelectionContainFormatting = (
    documentId: string,
    formattingKey: "bold" | "underline" | "strikeThrough" | "italic"
  ) => {
    const { selectedTokens } = useDocumentSelectionStore(documentId);

    var doAnyTokensHaveFormatting = false;

    for (var token of selectedTokens) {
      if (token == undefined) continue;

      if (token.formatting == undefined) continue;

      var tokenFormatting = token.formatting;
      var tokenFormattingKeys = Object.keys(tokenFormatting);

      if (
        tokenFormattingKeys.includes(formattingKey) &&
        tokenFormatting[formattingKey] === true
      ) {
        doAnyTokensHaveFormatting = true;
        break;
      }
    }

    return doAnyTokensHaveFormatting;
  };

  const selectedColor = (documentId: string) => {
    let color = [0, 0, 0];
    const { selectedTokens } = useDocumentSelectionStore(documentId);

    for (var token of selectedTokens) {
      if (token == undefined) continue;
      if (token.formatting && token.formatting.color) {
        color = token.formatting.color;
      }
    }

    return color;
  };

  const selectedHighlightColor = (documentId: string) => {
    let color = [0, 0, 0];
    const { selectedTokens } = useDocumentSelectionStore(documentId);

    for (var token of selectedTokens) {
      if (token == undefined) continue;

      if (
        token.formatting &&
        token.formatting.highlightColor &&
        typeof token.formatting.highlightColor != "string"
      ) {
        color = token.formatting.highlightColor;
      }
    }

    return color;
  };

  const alignSelectedParagraphs = (
    documentId: string,
    formattingChanges: ParagraphFormattingChanges
  ) => {
    const selectedParagraphs =
      useDocumentSelectionStore(documentId).selectedParagraphs;

    for (var paragraph of selectedParagraphs) {
      var currentFormatting = paragraph.formatting;
      var newTokenFormatting = {
        ...currentFormatting,
        ...formattingChanges,
      };

      paragraph.formatting = newTokenFormatting;
    }

    useDocumentParagraphsStore(documentId).saveParagraphs(
      selectedParagraphs,
      undefined,
      true
    );
  };

  // Hmm this is interesting. I don't care about a boolean value this time. I care about?
  // I want to check specific properties. So I can check if a key matches a particular value.
  const doSelectedParagraphsHaveAlignment = (
    documentId: string,
    value: "RIGHT" | "CENTER" | "LEFT" | number
  ) => {
    var paragraphs = useDocumentSelectionStore(documentId).selectedParagraphs;

    let hasFormatting = false;

    for (var paragraph of paragraphs) {
      if (
        paragraph.formatting.alignment &&
        paragraph.formatting.alignment == value
      ) {
        hasFormatting = true;
      }

      if (value == "LEFT" && !paragraph.formatting.alignment) {
        hasFormatting = true;
      }
    }

    return hasFormatting;
  };

  const indentationIncrement = 24;

  const indentSelectedParagraphs = (documentId: string) => {
    var selectedParagraphs =
      useDocumentSelectionStore(documentId).selectedParagraphs;

    for (var paragraph of selectedParagraphs) {
      if (!paragraph.formatting.leftIndent) {
        paragraph.formatting.leftIndent = 0;
      }

      paragraph.formatting.leftIndent += indentationIncrement;
    }

    useDocumentParagraphsStore(documentId).saveParagraphs(
      selectedParagraphs,
      undefined,
      true
    );
  };

  const unindentSelectedParagraphs = (documentId: string) => {
    var selectedParagraphs =
      useDocumentSelectionStore(documentId).selectedParagraphs;

    for (var paragraph of selectedParagraphs) {
      if (!paragraph.formatting.leftIndent) {
        paragraph.formatting.leftIndent = 0;
      }

      var newIndentation =
        paragraph.formatting.leftIndent - indentationIncrement;
      paragraph.formatting.leftIndent = Math.max(0, newIndentation);
    }

    useDocumentParagraphsStore(documentId).saveParagraphs(
      selectedParagraphs,
      undefined,
      true
    );
  };

  return {
    indentationIncrement,
    formatSelectedTokens,
    doesSelectionContainFormatting,
    selectedColor,
    selectedHighlightColor,
    alignSelectedParagraphs,
    doSelectedParagraphsHaveAlignment,
    indentSelectedParagraphs,
    unindentSelectedParagraphs,
  };
};
